import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, ArrowRight, Clock, Loader2, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "../lib/api";

const PAGE_SIZE = 24;

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [category, setCategory] = useState("all");
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");

  useEffect(() => {
    api.get("/blog/categories").then(r => setCategories(r.data.categories || [])).catch(()=>{});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    const params = { page, page_size: PAGE_SIZE };
    if (category !== "all") params.category = category;
    if (qDebounced) params.q = qDebounced;
    api.get("/blog", { params })
      .then(r => { setPosts(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, category, qDebounced]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const showingRange = useMemo(() => {
    if (!total) return "0";
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(total, page * PAGE_SIZE);
    return `${start.toLocaleString()}–${end.toLocaleString()} of ${total.toLocaleString()}`;
  }, [page, total]);

  const onChangeCategory = (c) => { setCategory(c); setPage(1); };

  return (
    <div>
      <section className="hero-blue py-20 px-5">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl sm:text-6xl font-extrabold text-white" style={{fontFamily:'Outfit'}}>
            <span style={{color:'var(--accent-yellow)'}}>Optivize</span> Blog
          </h1>
          <p className="text-blue-100 mt-4 text-lg">{total.toLocaleString()}+ articles on AI, web, SEO, ecommerce, payments, shipping and mobile.</p>
        </div>
      </section>

      <section className="py-10 px-5">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-6">
            <div className="relative w-full md:w-80">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"/>
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search articles…"
                className="input pl-9 w-full"
                data-testid="blog-search-input"
              />
            </div>
            <div className="text-xs text-slate-500" data-testid="blog-range">{showingRange}</div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6" data-testid="blog-categories">
            <button
              onClick={() => onChangeCategory("all")}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${category==="all" ? "bg-blue-600 text-white" : "bg-slate-800/60 text-slate-300 border border-slate-700 hover:border-blue-500"}`}
              data-testid="blog-cat-all"
            >
              All <span className="opacity-70">({total.toLocaleString()})</span>
            </button>
            {categories.map(([c, n]) => (
              <button
                key={c}
                onClick={() => onChangeCategory(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${category===c ? "bg-blue-600 text-white" : "bg-slate-800/60 text-slate-300 border border-slate-700 hover:border-blue-500"}`}
                data-testid={`blog-cat-${c.toLowerCase().replace(/[^a-z0-9]+/g,'-')}`}
              >
                {c} <span className="opacity-70">({n.toLocaleString()})</span>
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-blue-400" size={28}/></div>
          ) : posts.length === 0 ? (
            <div className="text-center text-slate-400 py-20">No posts match your filters.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p, i) => (
                <motion.article
                  key={p.slug}
                  initial={{opacity:0, y:20}} whileInView={{opacity:1, y:0}} viewport={{once:true}}
                  transition={{duration:.3, delay:Math.min(i,8)*0.04}}
                  className="card overflow-hidden flex flex-col group"
                  data-testid={`blog-card-${i}`}
                >
                  <Link to={`/blog/${p.slug}`} className="block relative aspect-[16/10] overflow-hidden">
                    <img src={p.cover} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 to-transparent" />
                    <span className="absolute top-3 left-3 tag !bg-blue-600 !text-white !border-blue-600">{p.category}</span>
                  </Link>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-3 text-xs text-slate-400">
                      <span className="inline-flex items-center gap-1"><Calendar size={12}/> {p.date}</span>
                      <span>·</span>
                      <span className="inline-flex items-center gap-1"><Clock size={12}/> {p.read} read</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mt-3" style={{fontFamily:'Outfit'}}>
                      <Link to={`/blog/${p.slug}`} className="hover:text-blue-300 transition-colors">{p.title}</Link>
                    </h3>
                    <p className="text-slate-400 mt-2 text-sm flex-1 line-clamp-3">{p.excerpt}</p>
                    <Link to={`/blog/${p.slug}`} className="mt-4 inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 text-sm font-semibold" data-testid={`blog-read-${i}`}>
                      Read more <ArrowRight size={14}/>
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10" data-testid="blog-pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-ghost !py-2 !px-3 disabled:opacity-40"
                data-testid="blog-prev"
              ><ChevronLeft size={14}/> Prev</button>
              <span className="text-sm text-slate-400 px-3" data-testid="blog-page-indicator">Page {page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-ghost !py-2 !px-3 disabled:opacity-40"
                data-testid="blog-next"
              >Next <ChevronRight size={14}/></button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
